import React, { Component } from "react";
import { withRouter } from "react-router-dom";

import Backend from "../utils/Backend";
import Notify from "../utils/Notify";
import Currency from "../utils/Currency";

import ImageUpload from "./components/common/ImageUpload";
import Select from "./components/common/Select";
import AddProductToBasketModal from "./components/modals/AddProductToBasketModal";

const BASKET_TYPE_OPTIONS = [
  { label: "Select Type", value: "" },
  { label: "Hamper", value: "hamper" },
  { label: "Party Basket", value: "party_basket" },
  { label: "Custom Bundle", value: "custom_bundle" },
];

class AddBasket extends Component {
  constructor(props) {
    super(props);

    const basketId = props.match?.params?.basketId;
    const isEditing = !!basketId;

    this.state = {
      basketId,
      isEditing,
      isLoading: false,
      isSaving: false,
      showAddProductModal: false,
      data: {
        name: "",
        description: "",
        type: "",
        image: null,
        price: "",
        discounted_price: "",
        is_active: true,
        min_purchase_qty: 1,
        max_purchase_qty: "",
        items: [],
      },
      calculatedPrice: 0,
    };
  }

  componentDidMount() {
    if (this.state.isEditing) {
      this._loadBasket();
    }
  }

  _loadBasket() {
    const { basketId } = this.state;

    this.setState({ isLoading: true });

    Backend.getBasket(basketId)
      .then((basket) => {
        this.setState({
          data: {
            name: basket.name || "",
            description: basket.description || "",
            type: basket.type || "",
            image: basket.image || null,
            price: basket.price || "",
            discounted_price: basket.discounted_price || "",
            is_active: basket.is_active !== undefined ? basket.is_active : true,
            min_purchase_qty: basket.min_purchase_qty || 1,
            max_purchase_qty: basket.max_purchase_qty || "",
            items: basket.items || [],
          },
          isLoading: false,
        });
        this._calculateTotalPrice(basket.items || []);
      })
      .catch((error) => {
        Notify.error(error.message || "Failed to load basket");
        this.setState({ isLoading: false });
      });
  }

  _handleChange = (e) => {
    const { data } = this.state;
    data[e.target.name] = e.target.value;
    this.setState({ data });
  };

  _handleSelectChange = (name, selectedOption) => {
    const { data } = this.state;
    data[name] = selectedOption?.value || "";
    this.setState({ data });
  };

  _handleImageUpdate = (image) => {
    const { data } = this.state;
    data.image = image;
    this.setState({ data });
  };

  _handleToggleActive = () => {
    const { data } = this.state;
    data.is_active = !data.is_active;
    this.setState({ data });
  };

  _handleAddProductFromModal = (item) => {
    // item comes from modal: { product, product_id, quantity, price }
    const { data } = this.state;
    
    data.items.push({
      product: item.product,
      product_id: item.product_id,
      quantity: item.quantity,
      price: item.price,
    });

    this.setState({ data, showAddProductModal: false });
    this._calculateTotalPrice(data.items);
  };

  _calculateTotalPrice(items) {
    const total = items.reduce((sum, item) => {
      const itemPrice = item.product?.price || item.price || 0;
      const qty = item.quantity || 1;
      return sum + itemPrice * qty;
    }, 0);
    this.setState({ calculatedPrice: total });
  }

  _addProduct = (product, quantity) => {
    const { data } = this.state;
    
    // Check if product already exists
    const existingIndex = data.items.findIndex(
      (item) => item.product?.id === product.id || item.product_id === product.id
    );

    if (existingIndex >= 0) {
      // Update quantity
      data.items[existingIndex].quantity += quantity;
    } else {
      // Add new item
      data.items.push({
        product: product,
        product_id: product.id,
        quantity: quantity,
        price: product.price,
      });
    }

    this.setState({ data, showAddProductModal: false });
    this._calculateTotalPrice(data.items);
  };

  _removeProduct = (index) => {
    const { data } = this.state;
    data.items.splice(index, 1);
    this.setState({ data });
    this._calculateTotalPrice(data.items);
  };

  _updateProductQuantity = (index, quantity) => {
    const { data } = this.state;
    if (quantity < 1) return;
    data.items[index].quantity = quantity;
    this.setState({ data });
    this._calculateTotalPrice(data.items);
  };

  _isFormValid() {
    const { data } = this.state;
    let error = null;

    if (!data.name?.trim()) {
      error = "Please enter a basket name.";
    } else if (!data.type) {
      error = "Please select a basket type.";
    } else if (!data.price || parseFloat(data.price) <= 0) {
      error = "Please enter a valid price.";
    } else if (data.items.length === 0) {
      error = "Please add at least one product to the basket.";
    }

    if (error) {
      Notify.error(error);
      return false;
    }

    return true;
  }

  _prepareBasketData() {
    const { data } = this.state;

    const basketData = {
      name: data.name.trim(),
      description: data.description?.trim() || "",
      type: data.type,
      price: parseFloat(data.price),
      is_active: data.is_active,
      min_purchase_qty: parseInt(data.min_purchase_qty) || 1,
    };

    if (data.image?.id) {
      basketData.image = data.image.id;
    }

    if (data.discounted_price) {
      basketData.discounted_price = parseFloat(data.discounted_price);
    }

    if (data.max_purchase_qty) {
      basketData.max_purchase_qty = parseInt(data.max_purchase_qty);
    }

    // Prepare items - backend expects 'item' field, not 'product_id'
    basketData.items = data.items.map((basketItem) => ({
      item: basketItem.product?.id || basketItem.product_id,
      quantity: basketItem.quantity,
    }));

    return basketData;
  }

  _saveBasket = () => {
    if (!this._isFormValid()) {
      return;
    }

    const { isEditing, basketId } = this.state;
    const basketData = this._prepareBasketData();

    this.setState({ isSaving: true });

    const savePromise = isEditing
      ? Backend.updateBasket(basketId, basketData)
      : Backend.createBasket(basketData);

    savePromise
      .then((basket) => {
        Notify.success(
          isEditing
            ? "Basket updated successfully!"
            : "Basket created successfully!"
        );
        this.props.history.push("/manage-baskets");
      })
      .catch((error) => {
        Notify.error(error.message || "Failed to save basket");
        this.setState({ isSaving: false });
      });
  };

  _goBack = () => {
    this.props.history.goBack();
  };

  _renderProductsList() {
    const { data } = this.state;

    if (data.items.length === 0) {
      return (
        <div className="text-center py-10">
          <p className="text-muted">No products added yet.</p>
          <button
            type="button"
            className="btn btn-light-primary"
            onClick={() => this.setState({ showAddProductModal: true })}
          >
            <i className="fa fa-plus mr-2"></i>
            Add Products
          </button>
        </div>
      );
    }

    return (
      <div className="table-responsive">
        <table className="table table-head-custom table-vertical-center">
          <thead>
            <tr>
              <th style={{ width: "50%" }}>Product</th>
              <th style={{ width: "20%" }}>Unit Price</th>
              <th style={{ width: "15%" }}>Quantity</th>
              <th style={{ width: "15%" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item, index) => {
              const product = item.product || {};
              const price = product.price || item.price || 0;

              return (
                <tr key={index}>
                  <td>
                    <div className="d-flex align-items-center">
                      {product.image?.thumbnail && (
                        <div className="symbol symbol-40 symbol-light mr-3">
                          <img
                            src={product.image.thumbnail}
                            alt={product.title}
                            className="symbol-label"
                            style={{ objectFit: "cover" }}
                          />
                        </div>
                      )}
                      <div>
                        <span className="text-dark-75 font-weight-bold">
                          {product.title || product.name || "Product"}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="text-dark-75">
                      {Currency.format(price)}
                    </span>
                  </td>
                  <td>
                    <div className="input-group input-group-sm" style={{ width: 100 }}>
                      <div className="input-group-prepend">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() =>
                            this._updateProductQuantity(index, item.quantity - 1)
                          }
                        >
                          -
                        </button>
                      </div>
                      <input
                        type="number"
                        className="form-control text-center"
                        value={item.quantity}
                        onChange={(e) =>
                          this._updateProductQuantity(
                            index,
                            parseInt(e.target.value) || 1
                          )
                        }
                        min="1"
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() =>
                            this._updateProductQuantity(index, item.quantity + 1)
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-icon btn-sm btn-light-danger"
                      title="Remove"
                      onClick={() => this._removeProduct(index)}
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const {
      data,
      isEditing,
      isLoading,
      isSaving,
      showAddProductModal,
      calculatedPrice,
    } = this.state;

    if (isLoading) {
      return (
        <div className="container">
          <div className="card card-custom">
            <div className="card-body text-center py-10">
              <div className="spinner spinner-primary spinner-lg"></div>
              <p className="mt-5">Loading basket...</p>
            </div>
          </div>
        </div>
      );
    }

    const selectedType = BASKET_TYPE_OPTIONS.find(
      (opt) => opt.value === data.type
    );

    return (
      <div className="container">
        <div className="card card-custom">
          {/* Header */}
          <div className="card-header">
            <div className="card-title">
              <h3 className="card-label">
                {isEditing ? "Edit Basket" : "Create New Basket"}
                <small className="text-muted ml-2">
                  {isEditing ? "Update basket details" : "Add a new hamper or party basket"}
                </small>
              </h3>
            </div>
            <div className="card-toolbar">
              <button
                type="button"
                className="btn btn-light-primary mr-2"
                onClick={this._goBack}
              >
                <i className="fa fa-arrow-left mr-2"></i>
                Back
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="card-body">
            <div className="row">
              {/* Left Column - Basic Info */}
              <div className="col-lg-8">
                {/* Basic Information */}
                <div className="form-group row">
                  <label className="col-lg-3 col-form-label">
                    Basket Name <span className="text-danger">*</span>
                  </label>
                  <div className="col-lg-9">
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      placeholder="e.g., Party Starter Pack"
                      value={data.name}
                      onChange={this._handleChange}
                    />
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-lg-3 col-form-label">Description</label>
                  <div className="col-lg-9">
                    <textarea
                      className="form-control"
                      name="description"
                      rows="3"
                      placeholder="Describe what's included in this basket..."
                      value={data.description}
                      onChange={this._handleChange}
                    />
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-lg-3 col-form-label">
                    Basket Type <span className="text-danger">*</span>
                  </label>
                  <div className="col-lg-9">
                    <Select
                      value={selectedType}
                      options={BASKET_TYPE_OPTIONS}
                      placeholder="Select basket type"
                      onChange={(option) =>
                        this._handleSelectChange("type", option)
                      }
                    />
                  </div>
                </div>

                {/* Pricing */}
                <div className="separator separator-dashed my-8"></div>
                <h5 className="text-dark font-weight-bold mb-6">Pricing</h5>

                <div className="form-group row">
                  <label className="col-lg-3 col-form-label">
                    Calculated Price
                  </label>
                  <div className="col-lg-9">
                    <div className="form-control-plaintext">
                      <span className="text-muted">
                        Based on products: {Currency.format(calculatedPrice)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-lg-3 col-form-label">
                    Bundle Price <span className="text-danger">*</span>
                  </label>
                  <div className="col-lg-9">
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">€</span>
                      </div>
                      <input
                        type="number"
                        className="form-control"
                        name="price"
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        value={data.price}
                        onChange={this._handleChange}
                      />
                    </div>
                    <span className="form-text text-muted">
                      The fixed price for this basket
                    </span>
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-lg-3 col-form-label">
                    Discounted Price
                  </label>
                  <div className="col-lg-9">
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">€</span>
                      </div>
                      <input
                        type="number"
                        className="form-control"
                        name="discounted_price"
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        value={data.discounted_price}
                        onChange={this._handleChange}
                      />
                    </div>
                    <span className="form-text text-muted">
                      Optional sale price (leave empty for no discount)
                    </span>
                  </div>
                </div>

                {/* Purchase Limits */}
                <div className="separator separator-dashed my-8"></div>
                <h5 className="text-dark font-weight-bold mb-6">
                  Purchase Restrictions
                </h5>

                <div className="form-group row">
                  <label className="col-lg-3 col-form-label">
                    Min Quantity
                  </label>
                  <div className="col-lg-4">
                    <input
                      type="number"
                      className="form-control"
                      name="min_purchase_qty"
                      min="1"
                      value={data.min_purchase_qty}
                      onChange={this._handleChange}
                    />
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-lg-3 col-form-label">
                    Max Quantity
                  </label>
                  <div className="col-lg-4">
                    <input
                      type="number"
                      className="form-control"
                      name="max_purchase_qty"
                      placeholder="No limit"
                      min="1"
                      value={data.max_purchase_qty}
                      onChange={this._handleChange}
                    />
                    <span className="form-text text-muted">
                      Leave empty for no limit
                    </span>
                  </div>
                </div>

                {/* Products Section */}
                <div className="separator separator-dashed my-8"></div>
                <div className="d-flex justify-content-between align-items-center mb-6">
                  <h5 className="text-dark font-weight-bold mb-0">
                    Products in Basket <span className="text-danger">*</span>
                  </h5>
                  {data.items.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-light-primary"
                      onClick={() => this.setState({ showAddProductModal: true })}
                    >
                      <i className="fa fa-plus mr-2"></i>
                      Add Product
                    </button>
                  )}
                </div>

                {this._renderProductsList()}
              </div>

              {/* Right Column - Image & Status */}
              <div className="col-lg-4">
                <div className="card card-custom card-stretch">
                  <div className="card-header">
                    <div className="card-title">
                      <h3 className="card-label">Basket Image</h3>
                    </div>
                  </div>
                  <div className="card-body text-center">
                    <div
                      className="image-input image-input-outline"
                      style={{ margin: "0 auto" }}
                    >
                      <ImageUpload
                        image={data.image}
                        hidePreview={false}
                        cropImage={true}
                        aspectRatio={1}
                        onUpdated={this._handleImageUpdate}
                        type="basket"
                      />
                    </div>
                    <span className="form-text text-muted mt-3">
                      Upload a basket image (recommended: 400x400px)
                    </span>
                  </div>
                   <div className="card card-custom card-stretch mt-15">
                  <div className="card-header">
                    <div className="card-title">
                      <h3 className="card-label">Visibility</h3>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-dark font-weight-bold">
                          Active Status
                        </span>
                        <p className="text-muted mb-0 font-size-sm">
                          {data.is_active
                            ? "Basket is visible to customers"
                            : "Basket is hidden from customers"}
                        </p>
                      </div>
                      <span
                        className={`switch switch-sm ${
                          data.is_active ? "switch-success" : ""
                        }`}
                      >
                        <label>
                          <input
                            type="checkbox"
                            checked={data.is_active}
                            onChange={this._handleToggleActive}
                          />
                          <span></span>
                        </label>
                      </span>
                    </div>
                    {/* Summary Card */}
                <div className="card card-custom card-stretch mt-5 bg-light-primary">
                  <div className="card-body">
                    <h5 className="text-dark font-weight-bold mb-4">Summary</h5>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Products:</span>
                      <span className="font-weight-bold">{data.items.length}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Calculated Value:</span>
                      <span className="font-weight-bold">
                        {Currency.format(calculatedPrice)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Bundle Price:</span>
                      <span className="font-weight-bold text-primary">
                        {Currency.format((parseFloat(data.price) || 0 ) * 100)}
                      </span>
                    </div>
                    {data.discounted_price && (
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Sale Price:</span>
                        <span className="font-weight-bold text-success">
                          {Currency.format(parseFloat(data.discounted_price) * 100)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                  </div>
                </div>
                </div>

               

                
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="card-footer">
            <div className="row">
              <div className="col-lg-8">
                <button
                  type="button"
                  className="btn btn-primary mr-2"
                  onClick={this._saveBasket}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-save mr-2"></i>
                      {isEditing ? "Update Basket" : "Create Basket"}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={this._goBack}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Add Product Modal */}
        <AddProductToBasketModal
          show={showAddProductModal}
          onHide={() => this.setState({ showAddProductModal: false })}
          onAdd={this._handleAddProductFromModal}
          existingProducts={data.items}
        />
      </div>
    );
  }
}

export default withRouter(AddBasket);
